import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Alert,
  Chip,
  Grid,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  TableChart as TableChartIcon,
  Code as CodeIcon,
  TextFields as TextFieldsIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';

const DocumentUploadForm = ({ data = [], onChange = () => {}, mode }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  // 支持的文件类型
  const supportedTypes = {
    'application/pdf': { icon: PictureAsPdfIcon, label: 'PDF', color: '#ef4444' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: DescriptionIcon, label: 'DOCX', color: '#3b82f6' },
    'text/plain': { icon: TextFieldsIcon, label: 'TXT', color: '#94a3b8' },
    'text/markdown': { icon: CodeIcon, label: 'MD', color: '#0ea5e9' },
    'text/html': { icon: CodeIcon, label: 'HTML', color: '#f97316' },
    'text/csv': { icon: TableChartIcon, label: 'CSV', color: '#22c55e' },
  };

  // 文件大小限制（MB）
  const maxFileSize = 15;

  // 根据模式获取文档类型说明
  const getDocumentTypeDescription = () => {
    if (mode === 'proposal') {
      return {
        title: t('yuyan_doc_title_proposal'),
        description: t('yuyan_doc_desc_proposal'),
        required: [t('yuyan_doc_required_proposal')],
        optional: [t('yuyan_doc_optional_proposal_1'), t('yuyan_doc_optional_proposal_2'), t('yuyan_doc_optional_proposal_3')]
      };
    } else if (mode === 'conclusion') {
      return {
        title: t('yuyan_doc_title_conclusion'),
        description: t('yuyan_doc_desc_conclusion'),
        required: [t('yuyan_doc_required_conclusion')],
        optional: [t('yuyan_doc_optional_conclusion_1'), t('yuyan_doc_optional_conclusion_2'), t('yuyan_doc_optional_conclusion_3')]
      };
    }
    return { 
      title: t('yuyan_step_upload'), 
      description: t('yuyan_doc_desc_proposal'), 
      required: [], 
      optional: [] 
    };
  };

  // 验证文件
  const validateFile = (file) => {
    const errors = [];
    const warnings = [];
    
    const fileName = file.name.toLowerCase();
    const isDoc = fileName.endsWith('.doc') && !fileName.endsWith('.docx');

    if (isDoc) {
      errors.push(t('yuyan_doc_err_doc'));
    }

    // 检查PPT文件 — 直接报错，不再产生无效的 warning
    const isPPT = fileName.endsWith('.ppt') || fileName.endsWith('.pptx') || 
                  file.type === 'application/vnd.ms-powerpoint' ||
                  file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    
    if (isPPT) {
      errors.push(t('yuyan_doc_err_ppt'));
    }

    // 检查 Excel 文件
    const isExcel = fileName.endsWith('.xls') || fileName.endsWith('.xlsx') ||
                    file.type === 'application/vnd.ms-excel' ||
                    file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (isExcel) {
      errors.push(t('yuyan_doc_err_excel'));
    }
    
    // 检查文件类型（排除已单独处理的格式）
    if (!isDoc && !isPPT && !isExcel) {
      const actualType = file.type || getFileTypeFromName(file.name);
      if (!supportedTypes[actualType]) {
        const inferredType = getFileTypeFromName(file.name);
        if (!supportedTypes[inferredType]) {
          errors.push(t('yuyan_doc_err_type'));
        }
      }
    }
    
    // 检查文件大小
    if (file.size > maxFileSize * 1024 * 1024) {
      errors.push(t('yuyan_doc_err_size', { max: maxFileSize }));
    }
    
    if (file.name.length > 100) {
      errors.push(t('yuyan_doc_err_name_long'));
    }
    
    return { errors, warnings };
  };

  // 根据文件名推断文件类型
  const getFileTypeFromName = (fileName) => {
    const ext = fileName.toLowerCase().split('.').pop();
    const typeMap = {
      'pdf': 'application/pdf',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'txt': 'text/plain',
      'md': 'text/markdown',
      'markdown': 'text/markdown',
      'mdx': 'text/markdown',
      'html': 'text/html',
      'htm': 'text/html',
      'csv': 'text/csv',
    };
    return typeMap[ext] || 'application/octet-stream';
  };

  // 获取文件图标
  const getFileIcon = (fileType, fileName) => {
    const actualType = fileType || getFileTypeFromName(fileName);
    const typeConfig = supportedTypes[actualType];
    if (typeConfig) {
      const IconComponent = typeConfig.icon;
      return <IconComponent sx={{ color: typeConfig.color }} />;
    }
    return <FileIcon sx={{ color: '#94a3b8' }} />;
  };

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 处理文件选择
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    processFiles(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 处理拖拽事件
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // 处理拖放文件
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      processFiles(files);
    }
  };

  // 统一处理文件逻辑
  const processFiles = (files) => {
    const newDocuments = [];
    
    files.forEach(file => {
      const actualType = file.type || getFileTypeFromName(file.name);
      
      const validation = validateFile(file);
      const documentItem = {
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        type: actualType,
        errors: validation.errors,
        warnings: validation.warnings,
        isValid: validation.errors.length === 0
      };
      newDocuments.push(documentItem);
    });
    
    onChange([...(Array.isArray(data) ? data : []), ...newDocuments]);
  };

  // 删除文档
  const removeDocument = (id) => {
    const currentData = Array.isArray(data) ? data : [];
    const docToRemove = currentData.find(doc => doc.id === id);
    
    if (docToRemove && docToRemove.isExisting) {
      const updatedData = currentData.map(doc => 
        doc.id === id ? { ...doc, isDeleted: true, isValid: false } : doc
      );
      onChange(updatedData);
    } else {
      onChange(currentData.filter(doc => doc.id !== id));
    }
  };

  // 触发文件选择
  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const docTypeInfo = getDocumentTypeDescription();
  
  const safeDocTypeInfo = {
    ...docTypeInfo,
    required: Array.isArray(docTypeInfo.required) ? docTypeInfo.required : [],
    optional: Array.isArray(docTypeInfo.optional) ? docTypeInfo.optional : []
  };
  
  const safeData = Array.isArray(data) ? data : [];
  const validDocuments = safeData.filter(doc => doc && doc.isValid && !doc.isDeleted) || [];
  const invalidDocuments = safeData.filter(doc => doc && !doc.isValid && !doc.isDeleted) || [];

  return (
    <Paper 
      sx={{ 
        p: 4, 
        backgroundColor: 'rgba(30, 41, 59, 0.4)', 
        backdropFilter: 'blur(12px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        mb: 3 
      }}
    >
      <Typography variant="h6" sx={{ color: '#e2e8f0', mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <FileIcon sx={{ color: '#38bdf8' }} />
        {safeDocTypeInfo.title}
      </Typography>
      
      <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
        {safeDocTypeInfo.description}
      </Typography>

      {/* 文档要求说明 */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ p: 2, borderRadius: '8px', background: 'rgba(255, 255, 255, 0.03)' }}>
            <Typography variant="subtitle2" sx={{ color: '#e2e8f0', mb: 1, fontWeight: 600 }}>
              {t('yuyan_doc_required_label')}
            </Typography>
            <List dense>
              {safeDocTypeInfo.required.map((req, index) => (
                <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#4ade80' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={req} 
                    primaryTypographyProps={{ fontSize: '0.875rem', color: '#94a3b8' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{ p: 2, borderRadius: '8px', background: 'rgba(255, 255, 255, 0.03)' }}>
            <Typography variant="subtitle2" sx={{ color: '#e2e8f0', mb: 1, fontWeight: 600 }}>
              {t('yuyan_doc_optional_label')}
            </Typography>
            <List dense>
              {safeDocTypeInfo.optional.map((opt, index) => (
                <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 24 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#94a3b8' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={opt} 
                    primaryTypographyProps={{ fontSize: '0.875rem', color: '#94a3b8' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Grid>
      </Grid>

      {/* 文件上传区域 */}
      <Box
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        sx={{
          border: `2px dashed ${dragActive ? '#38bdf8' : 'rgba(255, 255, 255, 0.1)'}`,
          borderRadius: 4,
          p: 6,
          textAlign: 'center',
          backgroundColor: dragActive ? 'rgba(56, 189, 248, 0.05)' : 'rgba(255, 255, 255, 0.02)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: '#38bdf8',
            backgroundColor: 'rgba(56, 189, 248, 0.05)'
          }
        }}
        onClick={triggerFileSelect}
      >
        <CloudUploadIcon sx={{ fontSize: 64, color: dragActive ? '#38bdf8' : '#64748b', mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#e2e8f0', mb: 1, fontWeight: 500 }}>
          {t('yuyan_doc_click_hint')}
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
          {t('yuyan_doc_format_hint', { max: maxFileSize })}
        </Typography>
        
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.5)',
            px: 4,
            py: 1,
            borderRadius: '8px',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.5)'
            }
          }}
        >
          {t('yuyan_doc_select_file')}
        </Button>
      </Box>

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.txt,.md,.markdown,.mdx,.html,.htm,.csv"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Word文档修订提示 */}
      <Alert severity="warning" sx={{ mt: 3, background: 'rgba(245, 158, 11, 0.1)', color: '#fbbf24', border: '1px solid rgba(245, 158, 11, 0.2)', '& .MuiAlert-icon': { color: '#fbbf24' } }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {t('yuyan_doc_word_hint')}
        </Typography>
      </Alert>

      {/* 已上传文件列表 */}
      {safeData.length > 0 && (
        <Box mt={4}>
          <Typography variant="subtitle1" sx={{ color: '#e2e8f0', mb: 2, fontWeight: 600 }}>
            {t('yuyan_doc_file_list', { count: validDocuments.length })}
          </Typography>
          
          <Grid container spacing={2}>
            {/* 有效文件 */}
            {validDocuments.map((doc) => (
              <Grid item xs={12} sm={6} key={doc.id}>
                <Paper sx={{ 
                  p: 2, 
                  background: doc.isExisting ? 'rgba(34, 197, 94, 0.05)' : 'rgba(255, 255, 255, 0.03)', 
                  border: doc.isExisting ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <Box sx={{ p: 1, borderRadius: '8px', background: 'rgba(255,255,255,0.05)' }}>
                    {getFileIcon(doc.type, doc.name)}
                  </Box>
                  <Box flex={1} overflow="hidden">
                    <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 500, mb: 0.5 }} noWrap>
                      {doc.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {formatFileSize(doc.size)}
                      </Typography>
                      {doc.isExisting && (
                        <Chip label={t('yuyan_doc_uploaded')} size="small" sx={{ height: 16, fontSize: 10, bgcolor: 'rgba(34, 197, 94, 0.2)', color: '#4ade80' }} />
                      )}
                      {!doc.isExisting && (
                        <Chip label={t('yuyan_doc_pending')} size="small" sx={{ height: 16, fontSize: 10, bgcolor: 'rgba(56, 189, 248, 0.2)', color: '#38bdf8' }} />
                      )}
                    </Box>
                    {doc.warnings && doc.warnings.length > 0 && (
                      <Typography variant="caption" sx={{ color: '#fbbf24', display: 'block', mt: 0.5 }}>
                        ⚠ {doc.warnings[0]}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => removeDocument(doc.id)}
                    sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', background: 'rgba(239, 68, 68, 0.1)' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              </Grid>
            ))}

            {/* 无效文件 */}
            {invalidDocuments.map((doc) => (
              <Grid item xs={12} sm={6} key={doc.id}>
                <Paper sx={{ 
                  p: 2, 
                  background: 'rgba(239, 68, 68, 0.05)', 
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <ErrorIcon sx={{ color: '#ef4444' }} />
                  <Box flex={1} overflow="hidden">
                    <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 500 }} noWrap>
                      {doc.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#fca5a5' }}>
                      {doc.errors && doc.errors[0]}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => removeDocument(doc.id)}
                    sx={{ color: '#ef4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default DocumentUploadForm;